import { createFileRoute, notFound, useLocation } from "@tanstack/react-router";
import { ActivityRouteView } from "@/components/activity/activity-route-view";
import { parseMemorySplat } from "@/lib/memory-browse";
import { isReservedOwnerSegment } from "@/lib/reserved";
import { entitySplatFromPathname } from "@/lib/resolve-entity-path";
import { pruneSearch } from "@/lib/router-search";

/**
 * One record's activity, full width: `/{owner}/events/<type>/<slug>[/…]`.
 *
 * The scope is the path, not a filter — this used to be `/events?entity_id=N`,
 * which could not name the record it was scoped to (the header read the name
 * off the first result row, so a record with no events said "this entity").
 * A path resolves to the record whether or not it has history.
 *
 * The record's page (`/{owner}/<type>/<slug>`) opens the same feed in the peek
 * pane; this is the same content at full width, which is what the pane's
 * open-full-page action points at.
 */
export const Route = createFileRoute("/$owner/events/$")({
	beforeLoad: ({ location }) => {
		const splat = parseMemorySplat(
			entitySplatFromPathname(location.pathname, 1),
		);
		// A bare type, a reserved owner segment, or an empty splat addresses no
		// record — there is nothing to scope the stream to.
		if (
			!splat.typeSegment ||
			!splat.isEntityDetail ||
			isReservedOwnerSegment(splat.typeSegment)
		) {
			throw notFound();
		}
	},
	component: EntityActivityPage,
	validateSearch: (search: Record<string, unknown>) =>
		pruneSearch(search) as Record<string, string | undefined>,
});

function EntityActivityPage() {
	const { owner } = Route.useParams();
	const pathname = useLocation({ select: (location) => location.pathname });
	const search = Route.useSearch();

	return (
		<ActivityRouteView
			owner={owner}
			search={search}
			entityScope={parseMemorySplat(entitySplatFromPathname(pathname, 1)).path}
		/>
	);
}
